import BigNumber from 'bignumber.js'
import { DISPLAY_AMOUNT } from 'config/constants'
import { RouteConfig } from 'config/constants/route'
import { BetDetails } from 'config/types/profile'
import useOpenBetDetails from 'hooks/useOpenBetDetails'
import { RowCenter, RowMiddle } from 'layout/Components/Row'
import React, { useCallback, useState } from 'react'
import { useUserSettings } from 'state/profile/hooks'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import { colors } from 'theme/colors'
import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import Link from 'UIKit/Link'
import Text from 'UIKit/Text'
import TokenLogo from 'UIKit/TokenLogo'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { formatRoute } from 'utils/stringHelper'
import TokenAmountFiatDisplay from 'views/Fiat/TokenAmountFiatDisplay'
import UserDisplay from 'views/Profile/components/UserDisplay'
import { LIVE_WIN_ANIMATION_DURATION } from '../config'

interface LiveWinCardProps extends BoxProps {
  betDetails: BetDetails
  className?: string
  animationRef?: {
    disabled?: boolean
  }
}

const LiveWinCard: React.FC<LiveWinCardProps> = ({
  betDetails: { game, player, payout, betId },
  className,
  animationRef = { disabled: true },
  ...props
}) => {
  const {
    userSettings: { isDisplayInFiat },
  } = useUserSettings()
  const { presentBetModal } = useOpenBetDetails()
  const [isHovered, setIsHovered] = useState(false)

  const handleRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.style.setProperty('--card-width', `${node.offsetWidth}px`)
    }
  }, [])

  const onModalDetails = useCallback(() => {
    presentBetModal(betId)
  }, [betId])

  const handleImageClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  return (
    <StyleBox className={`${className} ${animationRef.disabled ? 'no-animation' : ''}`} ref={handleRef}>
      <StyledCard onClick={onModalDetails} {...props}>
        <Box onClick={handleImageClick}>
          <GameImageWrapper
            href={`${formatRoute(RouteConfig.GameDetail, { slug: game.slug })}`}
            onMouseOver={() => setIsHovered(true)}
            onMouseOut={() => setIsHovered(false)}
            disabledDefaultStyle
          >
            <Image
              src={game.thumbnail}
              width={300}
              height={300}
              alt={game.name}
              layout="fill"
              objectFit="cover"
              blurLoader
            />

            <StyledHoverWrapper isHovered={isHovered}>
              <StyledPlayGame>
                <Icons.PlayAltIcon width="16px" height="16px" fill={colors.buttonBackgroundAlt2} />
              </StyledPlayGame>
            </StyledHoverWrapper>
          </GameImageWrapper>
        </Box>

        <Box paddingTop={16}>
          <RowMiddle justifyContent="center" width="100%" paddingBottom="8px">
            <StyledUserDisplay user={{ ...player, code: null }} overflow={'hidden'} ellipsis={1} />
          </RowMiddle>

          <RowMiddle justifyContent="center" width="100%">
            <TokenLogo token={payout.token} size={20} networkImageProps={{ width: 8, height: 8 }} />
            <WinAmount ml="4px" bold ellipsis={1} display="block">
              {isDisplayInFiat ? (
                <TokenAmountFiatDisplay
                  tokenAmount={{
                    token: payout.token,
                    amount: BigNumber(payout.amount),
                  }}
                />
              ) : (
                getFullDisplayBalance(BigNumber(payout.amount), 0, DISPLAY_AMOUNT)
              )}
            </WinAmount>
          </RowMiddle>
        </Box>
      </StyledCard>
    </StyleBox>
  )
}

const sharedStyles = css`
  --card-image-radius: ${({ theme }) => theme.radii.small};
  --card-padding: 8px;
  --card-radius: calc(var(--card-image-radius) + var(--card-padding));
`

const StyleBox = styled(Box)`
  &.stream-paused {
    scroll-snap-align: start;
    scroll-snap-stop: normal;
  }

  &:not(.no-animation) {
    animation: new-card-appear ${LIVE_WIN_ANIMATION_DURATION / 1000}s;
  }

  @keyframes new-card-appear {
    0% {
      margin-left: calc(-1 * var(--card-width, 0px));
    }
    100% {
      margin-left: 0px;
    }
  }
`

const StyledCard = styled(Box)`
  ${sharedStyles}

  cursor: pointer;
  width: 100%;
  height: max-content;
  border-radius: var(--card-radius);
  overflow: hidden;
  padding: var(--card-padding);
  background: rgba(255, 255, 255, 0.06);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`

const GameImageWrapper = styled(Link)`
  ${sharedStyles}

  overflow: hidden;
  border-radius: var(--card-image-radius);
  position: relative;
  display: block;
`

const StyledUserDisplay = styled(UserDisplay)`
  cursor: pointer !important;
  font-size: 14px;
`

const WinAmount = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.success};
`

const StyledHoverWrapper = styled(RowCenter)<{ isHovered: boolean }>`
  z-index: 1;
  top: 0px;
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: ${({ isHovered }) => (isHovered ? 1 : 0)};
  background: linear-gradient(180deg, rgba(22, 28, 36, 0) -22.78%, #191d25e0 55%, #17181b 86.75%);
  transition: ${({ theme }) => theme.transitions.medium};
`

const StyledPlayGame = styled(Box)`
  background: ${({ theme: { colors } }) => colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme: { radii } }) => radii.tiny} !important;
`

export default React.memo(LiveWinCard)
